import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import { Bar, BarChart, Cell, ResponsiveContainer, XAxis, YAxis } from 'recharts';

const ORANGE = 'rgb(203, 100, 35)';
const BAR_MAX = 68;

const AxisTick = ({ x, y, payload }) => (
  <text
    x={x}
    y={y}
    dy="0.355em"
    textAnchor="end"
    fill="#555"
    fontSize={10}
    fontWeight={400}
    fontFamily="inherit">
    {payload.value}
  </text>
);

const CustomBar = ({ x, y, width, height, payload, fill }) => (
  <g>
    <rect x={x} y={y} width={width} height={height} fill={fill} />

    <text
      x={x + width + 4}
      y={y + height / 2}
      dy="0.355em"
      textAnchor="start"
      fill="#555"
      fontSize={10}
      fontWeight={400}
      fontFamily="inherit">
      {payload.label}
    </text>
  </g>
);

export default function PlatformAUMChart() {
  const theme = useTheme();

  const { fullBook } = useSelector((state) => state.aum);

  const data = useMemo(() => {
    if (!fullBook?.length) return [];

    return [...(fullBook[0].providers || [])]
      .sort((a, b) => b.amount - a.amount)
      .map((provider) => ({
        name: provider.name,
        value: provider.amount / 1_000_000_000
      }));
  }, [fullBook]);

  const chartData = useMemo(() => {
    const maxValue = Math.max(...data.map((d) => d.value), 1);

    return data.map((d) => ({
      ...d,
      chartValue: (d.value / maxValue) * BAR_MAX,
      label: `R${d.value.toFixed(2)}B`
    }));
  }, [data]);

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #ECECEC',
        boxShadow: '0 2px 12px rgba(0,0,0,.05)',
        height: 260
      }}>
      <CardContent
        sx={{
          p: 2,
          height: '100%',
          '&:last-child': {
            pb: 2
          }
        }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 12,
            mb: 1.5
          }}>
          AUM by Platform / MANCO
        </Typography>

        <Box
          sx={{
            height: 'calc(100% - 34px)'
          }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              barGap={0}
              barCategoryGap="2%"
              margin={{
                top: 2,
                right: 48,
                left: 0,
                bottom: 2
              }}>
              <XAxis type="number" hide domain={[0, BAR_MAX + 15]} />

              <YAxis
                type="category"
                dataKey="name"
                width={82}
                axisLine={{
                  stroke: '#D9D9D9',
                  strokeWidth: 1
                }}
                tickLine={false}
                tick={<AxisTick />}
              />

              <Bar
                dataKey="chartValue"
                barSize={20}
                radius={[0, 0, 0, 0]}
                shape={<CustomBar />}
                isAnimationActive>
                {chartData.map((entry, index) => (
                  <Cell
                    key={entry.name}
                    fill={index === chartData.length - 1 ? '#101418' : ORANGE}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}
